import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const UPDATED_AT = '22 luglio 2026';
const SUPPORT_EMAIL = 'supporto@leaguehub.it';

const LegalLayout = ({ title, subtitle, icon: Icon, children }) => (
  <main className="safe-public-page bg-background text-foreground">
    <div className="mx-auto max-w-3xl px-4 py-8 md:py-14">
      <Button asChild variant="ghost" className="mb-5 -ml-3">
        <Link to="/">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Torna a LeagueHub
        </Link>
      </Button>
      <div className="mb-8 flex items-start gap-4">
        <div className="rounded-2xl bg-primary/10 p-3 text-primary">
          <Icon className="h-7 w-7" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{title}</h1>
          <p className="mt-2 text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-6 text-sm leading-7 md:text-base">{children}</div>
    </div>
  </main>
);

const Section = ({ title, children }) => (
  <section>
    <h2 className="mb-2 text-xl font-semibold">{title}</h2>
    <div className="space-y-3 text-muted-foreground">{children}</div>
  </section>
);

export const PrivacyPolicyPage = () => (
  <LegalLayout
    title="Informativa sulla privacy"
    subtitle={`Ultimo aggiornamento: ${UPDATED_AT}`}
    icon={ShieldCheck}
  >
    <p>
      LeagueHub tratta solo i dati necessari a creare l&apos;account, organizzare leghe e partite,
      mostrare statistiche sportive e fornire le funzionalità richieste dall&apos;utente.
    </p>

    <Section title="Dati trattati">
      <ul className="list-disc space-y-1 pl-5">
        <li>dati account: nome, email, telefono facoltativo, immagine profilo e credenziali protette;</li>
        <li>profilo sportivo: città, sport, livello, disponibilità, statistiche e preferenze privacy;</li>
        <li>attività nell&apos;app: leghe, partite, squadre, risultati, commenti, media e notifiche;</li>
        <li>dati tecnici indispensabili per sicurezza, sessione e diagnostica;</li>
        <li>dati di pagamento solo quando si utilizzano funzioni a pagamento, gestiti dal fornitore di pagamento.</li>
      </ul>
    </Section>

    <Section title="Finalità e base giuridica">
      <p>
        I dati sono utilizzati per eseguire il servizio richiesto, proteggere account e piattaforma,
        gestire assistenza e comunicazioni operative e adempiere agli obblighi di legge. Le
        comunicazioni facoltative vengono inviate solo quando abilitate dall&apos;utente.
      </p>
    </Section>

    <Section title="Condivisione e fornitori">
      <p>
        Non vendiamo dati personali. Possiamo utilizzare fornitori tecnici per hosting, accesso
        Google/Apple, mappe, email, notifiche, messaggistica e pagamenti. Ricevono esclusivamente i
        dati necessari al servizio e operano secondo i rispettivi obblighi di protezione dei dati.
      </p>
    </Section>

    <Section title="Visibilità nell’app">
      <p>
        I membri delle leghe possono vedere le informazioni necessarie all&apos;organizzazione sportiva.
        Email, telefono, città, biografia, statistiche e disponibilità possono essere controllati da
        Profilo → Privacy. Le leghe e le partite contrassegnate come pubbliche sono visibili agli altri utenti.
      </p>
    </Section>

    <Section title="Conservazione e cancellazione">
      <p>
        Conserviamo i dati finché l&apos;account è attivo o per il tempo richiesto dalla legge. Dal profilo
        è possibile eliminare definitivamente l&apos;account e i dati personali. I risultati sportivi e i
        documenti contabili che devono essere conservati restano soltanto in forma non riconducibile
        all&apos;account eliminato.
      </p>
      <p>
        Consulta anche la pagina <Link className="text-primary underline" to="/account-deletion">Cancellazione account</Link>.
      </p>
    </Section>

    <Section title="Diritti e contatti">
      <p>
        Puoi chiedere accesso, rettifica, portabilità, limitazione o cancellazione dei dati scrivendo a{' '}
        <a className="text-primary underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        Puoi inoltre rivolgerti all&apos;autorità di controllo competente.
      </p>
    </Section>
  </LegalLayout>
);

export const AccountDeletionPage = () => (
  <LegalLayout
    title="Cancellazione account"
    subtitle="Elimina il tuo account LeagueHub e i dati personali associati"
    icon={Trash2}
  >
    <Card>
      <CardHeader>
        <CardTitle>Dall’app LeagueHub</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-muted-foreground">
        <ol className="list-decimal space-y-2 pl-5">
          <li>Accedi al tuo account.</li>
          <li>Apri Profilo dal menu dell&apos;app.</li>
          <li>Seleziona “Elimina account” nella parte superiore.</li>
          <li>Scrivi ELIMINA e conferma.</li>
        </ol>
        <p>La disconnessione è immediata e l&apos;operazione non può essere annullata.</p>
      </CardContent>
    </Card>

    <Section title="Se non riesci ad accedere">
      <p>
        Invia la richiesta dall&apos;indirizzo email associato all&apos;account. Potremmo chiederti una verifica
        dell&apos;identità prima di procedere, per evitare cancellazioni non autorizzate.
      </p>
      <Button asChild>
        <a href={`mailto:${SUPPORT_EMAIL}?subject=Richiesta%20cancellazione%20account%20LeagueHub`}>
          Richiedi la cancellazione via email
          <ExternalLink className="ml-2 h-4 w-4" />
        </a>
      </Button>
    </Section>

    <Section title="Cosa viene eliminato">
      <p>
        Account, credenziali, profili, preferenze, iscrizioni attive, notifiche, contenuti social e media
        caricati. Gli eventuali dati che devono essere conservati per obblighi legali e i risultati storici
        restano esclusivamente in forma anonimizzata.
      </p>
    </Section>
  </LegalLayout>
);
